import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Box, Typography } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { isSameReligion, normaliseReligion } from 'src/util/religion';

const ReligionInput = ({
  options = [],
  value,
  onChange,
  disabled = false,
  label = 'Religion',
  size
}) => {
  const t = useAutoTranslation();

  const selected = normaliseReligion(value);
  const resolvedOptions = React.useMemo(() => {
    if (!selected) return options;
    return options.some((o) => isSameReligion(o, selected))
      ? options
      : [...options, selected];
  }, [options, selected]);

  return (
    <Autocomplete
      options={resolvedOptions}
      value={selected}
      disabled={disabled}
      size={size}
      autoHighlight
      getOptionLabel={(option) => option?.display || ''}
      isOptionEqualToValue={isSameReligion}
      onChange={(_event, next) => onChange(normaliseReligion(next))}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps;
        return (
          <Box component="li" key={key} {...rest}>
            {/* `inherit`, not a variant. A variant sets its own size, and
                body2 rendered these options at 0.875rem while every other
                picker in the person form (Ethnicity among them) uses MUI's
                default option rendering and inherits the li's 1rem. The wrapper
                is here only for `noWrap`, since a religion display name can be
                long, so it should contribute truncation and nothing else. */}
            <Typography variant="inherit" noWrap>
              {option.display}
            </Typography>
          </Box>
        );
      }}
      renderInput={({ slotProps: inputSlotProps, ...params }) => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          sx={{
            '& .MuiAutocomplete-input': { paddingRight: '56px !important' }
          }}
          slotProps={{
            ...inputSlotProps,
            htmlInput: {
              ...inputSlotProps?.htmlInput,
              'aria-label': t(label) || label,
              title: selected?.display || ''
            }
          }}
        />
      )}
    />
  );
};

export default ReligionInput;
